import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Inline,
  InlineItem,
  Input,
  Placeholder,
  Stack,
  useKeyboardShortcut,
} from '@kinesis/bungle';
import { noop, isEmpty } from 'lodash/fp';
import Fuse from 'fuse.js';
import { PathwayLight } from '@/types';
import { pathwayPath } from '@/links';
import { shortcuts } from '@/shortcuts';
import { Item } from './item';
import { LegacyPathwaysItem } from './legacy-pathways-item';

const fuseOptions = {
  isCaseSensitive: false,
  shouldSort: true,
  includeMatches: false,
  findAllMatches: true,
  minMatchCharLength: 0,
  keys: ['name', 'id'],
};

// Pure list of pathways for a community, with name search — mirrors
// `disclosures-list`. The page owns the RTK call and passes the data down;
// this component is store-less + Storybook-renderable per the front-end
// component/page split. Fuzzy search over name/id; Enter opens the top
// match; Escape clears.
const PathwaysList = (props: PathwaysListProps) => {
  const {
    pathways,
    inventoryId,
    now,
    legacyPathwaysExist = false,
    onDelete = noop,
  } = props;
  const navigate = useNavigate();
  const ref = useKeyboardShortcut({
    predicate: shortcuts.SEARCH_ITEMS.handler,
    callback: (event) => {
      event.preventDefault();
      ref.current?.focus();
    },
  });
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (isEmpty(search)) return pathways;
    const fuse = new Fuse(pathways, fuseOptions);
    return fuse.search(search).map(({ item }) => item);
  }, [search, pathways]);
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setSearch('');
        ref.current?.blur();
      } else if (event.key === 'Enter' && filtered.length > 0) {
        navigate(pathwayPath(inventoryId, filtered[0].id));
      }
    },
    [ref, navigate, filtered, inventoryId],
  );

  return (
    <Stack space='medium'>
      <Inline>
        <InlineItem sizing='fill-container'>
          <Input
            ref={ref as any}
            type='search'
            placeholder={`Search (${shortcuts.SEARCH_ITEMS.mnemonic})`}
            onChange={setSearch}
            onKeyDown={onKeyDown as any}
            value={search}
            magnitude='large'
            autoFocus
          />
        </InlineItem>
      </Inline>
      {/* The legacy surface is a synthetic, pinned entry — not a searchable
          `Pathway` — so it shows only in the unfiltered list, at the top. */}
      {legacyPathwaysExist && isEmpty(search) && (
        <LegacyPathwaysItem inventoryId={inventoryId} />
      )}
      {filtered.map((pathway) => (
        <Item
          key={pathway.id}
          pathway={pathway}
          now={now}
          onDelete={onDelete}
        />
      ))}
      {isEmpty(search) && isEmpty(filtered) && !legacyPathwaysExist && (
        <Placeholder>There are no pathways.</Placeholder>
      )}
      {!isEmpty(search) && isEmpty(filtered) && (
        <Placeholder>There are no matching pathways.</Placeholder>
      )}
    </Stack>
  );
};

export type PathwaysListProps = {
  pathways: PathwayLight[];
  inventoryId: number;
  now: string;
  // The inventory has legacy pathway baseline data — render the synthetic,
  // non-deletable entry linking to the untouched legacy pages.
  legacyPathwaysExist?: boolean;
  onDelete?: (pathway: number) => void;
};

export { PathwaysList };
